/**
 * Helpers de presentación del lobby. PUROS y sin DOM, para que la UI mock y la
 * real compartan el mismo formato (bandera, tier de ping, etiquetas) y para
 * poder testearlos headless con `bun test`.
 */
import { RegionId, RoomStatus, PlayerIdentity, CountryCode } from "./contract";

/** ISO 3166-1 alpha-2 → emoji de bandera (indicadores regionales). */
export function countryToFlag(cc: string): string {
  const code = cc.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "🏳️";
  const BASE = 0x1f1e6; // 'A' regional indicator
  return String.fromCodePoint(
    BASE + (code.charCodeAt(0) - 65),
    BASE + (code.charCodeAt(1) - 65),
  );
}

export type PingTier = "good" | "ok" | "bad" | "unknown";

/**
 * Clasifica el RTT medido para colorear el badge. Umbrales alineados con el
 * objetivo de game feel (§5.1): ≤60 ms "se siente local", 60–120 jugable,
 * >120 penaliza. `null` = región aún no medida.
 */
export function pingTier(ms: number | null): PingTier {
  if (ms === null) return "unknown";
  if (ms <= 60) return "good";
  if (ms <= 120) return "ok";
  return "bad";
}

const REGION_LABELS: Record<RegionId, string> = {
  "us-east": "US Este",
  "us-west": "US Oeste",
  "eu-west": "EU Oeste",
  "eu-central": "EU Central",
  "sa-east": "Sudamérica",
  "ap-south": "Asia Sur",
  "ap-southeast": "Asia SE",
};

export function regionLabel(region: RegionId): string {
  return REGION_LABELS[region] ?? region;
}

const STATUS_LABELS: Record<RoomStatus, string> = {
  waiting: "ESPERANDO",
  starting: "INICIANDO",
  "in-progress": "EN JUEGO",
  full: "LLENA",
};

export function statusLabel(status: RoomStatus): string {
  return STATUS_LABELS[status] ?? status;
}

/** Nombre que se enseña mientras el jugador no ha elegido el suyo. */
export const GUEST_PLACEHOLDER = "INVITADO";

/**
 * Genera una identidad de invitado (§5.2: jugar antes de registrarse).
 *
 * `accountId` es placeholder local; en M2 lo emite el backend y se linkea a
 * Google/Apple/Steam conservando skins y progreso.
 *
 * Ya NO inventa un nombre tipo "Disco#4821" ni un país: el nombre lo elige la
 * persona en la pantalla de entrada (y se fija con `setPlayerName`), y el país
 * no lo sabe el cliente. Enseñar una bandera al azar en la cabecera del lobby
 * era decorado: parecía un dato y no lo era.
 */
export function makeGuestIdentity(country?: CountryCode): PlayerIdentity {
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return {
    accountId: `guest_${uuid}`,
    displayName: GUEST_PLACEHOLDER,
    isGuest: true,
    country,
  };
}
